import { CableCar, ExternalLink, Snowflake } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { placeLinks } from "../utils/placeLink";
import type { UiLang, SkiInfo } from "../lib/types";
import { PlaceAltLink } from "./PlaceAltLink";

interface Props {
  ski: SkiInfo;
  lang: UiLang;
  /** Stoppet stedene ligger ved. Brukes bare i søkelenka. */
  near?: string;
}

/**
 * Vintertilbudet rundt et stopp.
 *
 * Norge er en vinterdestinasjon halve året, og appen sa ingenting om det —
 * en plan for Geilo i februar nevnte ikke at det ligger et skianlegg der.
 *
 * Heisene får et tall — «14 heiser» sier hvor stort anlegget er på en måte
 * navnet på hver enkelt heis aldri ville gjort. Langrenn står ved siden av
 * alpint, ikke under, fordi Norge går like mye på ski i sporet som i bakken.
 */
export function SkiPanel({ ski, lang, near }: Props) {
  const t = dictionaries[lang];

  /*
   * Heisene får et tall, løypene bare et navn. OSM deler en løype i mange
   * veisegmenter, så en telling ville gitt «6524 langrennsløyper» for Oslo —
   * antall biter, ikke antall løyper.
   */
  const counts = [
    ski.lifts > 0 && { icon: CableCar, text: t.skiLifts(ski.lifts) },
    ski.hasDownhill && { icon: Snowflake, text: t.skiDownhill },
    ski.hasNordic && { icon: Snowflake, text: t.skiNordic },
  ].filter(Boolean) as { icon: typeof CableCar; text: string }[];

  return (
    <div className="ski">
      <div className="ski-counts">
        {counts.map(({ icon: Icon, text }) => (
          <span className="ski-count" key={text}>
            <Icon size={13} />
            {text}
          </span>
        ))}
      </div>

      {ski.areas.length > 0 && (
        <ul className="ski-areas">
          {ski.areas.map((area) => {
            const links = placeLinks(area, near);
            return (
              <li key={area.id}>
                {/*
                  Også de uten nettside skal kunne åpnes, og de *med* nettside
                  skal ha en vei videre om den ikke svarer — se
                  utils/placeLink.ts. Et lite skianlegg som ikke åpnet i vinter,
                  har gjerne en side som ikke åpner heller.
                */}
                <a
                  href={links.primary.href}
                  target="_blank"
                  rel="noreferrer"
                  title={area.name}
                >
                  {area.name} <ExternalLink size={10} />
                </a>
                <PlaceAltLink link={links.fallback} lang={lang} />
                <span className="ski-distance">{area.distanceKm} km</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
